/**
 * CheckAllPanel — TZ 2026-09-23 Блок 10: «Проверить всё».
 *
 * Кнопка запуска POST /admin/check-all (пробивает каждую модель,
 * привязанную к каждому модулю на каждом тарифе) и попап-таблица:
 * модель | где используется (модуль × тариф) | статус ок/ошибка с текстом
 * | «Перейти» → cs:settings-jump (таб «Конфиги модулей» → таб операции →
 * подсветка карточки в ModuleConfigsAdmin).
 *
 * Backend поддерживает 300с кэш после прогона (cached=true) — это показываем.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck, XCircle } from 'lucide-react';

import { useCheckAll, type CheckAllItem } from '@/hooks/useAdmin';
import { Modal } from '@/components/ui/Modal';
import { providerLabel } from '@/components/admin/providerMeta';
import { sendSettingsJump } from '@/components/admin/settingsJump';
import { cn } from '@/lib/utils';
const JUMP_TAB = 'moduleConfigs';

export function CheckAllPanel() {
  const { t } = useTranslation();
  const check = useCheckAll();
  const [open, setOpen] = useState(false);

  const openPopup = () => {
    setOpen(true);
    if (!check.data && !check.isPending) check.mutate();
    return undefined;
  };

  const goTo = (item: CheckAllItem) => {
    // 'Перейти' — на конфиг модуля×тарифа, который проверяли.
    setOpen(false);
    sendSettingsJump({
      tab: JUMP_TAB,
      moduleKey: item.module_key,
      tier: item.tier_key,
    });
  };

  const cached = check.data?.cached ?? false;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="btn-secondary flex items-center gap-2"
        onClick={openPopup}
        disabled={check.isPending}
      >
        {check.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ShieldCheck className="w-4 h-4" />
        )}
        {check.isPending
          ? t('system.checkAll.running', { defaultValue: 'Проверяю модели…' })
          : t('system.checkAll.button', { defaultValue: 'Проверить всё' })
        }
      </button>
      {cached && !check.isPending && <span className="text-xs text-surface-500">
        {t('system.checkAll.cached', { defaultValue: '(кэш последнего прогона)' })}
      </span>}
      {check.isError && (
        <span className="text-xs text-danger">
          {t('system.checkAll.failed', { defaultValue: 'Не удалось запустить проверку.' })}
        </span>
      )}
      <CheckAllModal
        open={open}
        data={check.data}
        isPending={check.isPending}
        onClose={() => setOpen(false)}
        onGoTo={goTo}
      />
    </div>
  );
}

function CheckAllModal({
  open,
  data,
  isPending,
  onClose,
  onGoTo,
}: {
  open: boolean;
  data: ReturnType<typeof useCheckAll>['data'];
  isPending: boolean;
  onClose: () => void;
  onGoTo: (item: CheckAllItem) => void;
}) {
  const { t } = useTranslation();
  const results = data?.results ?? [];
  const okCount = data?.ok_count ?? 0;
  const failCount = data?.fail_count ?? 0;
  const cached = data?.cached ?? false;
  const checkedAt = data?.checked_at
    ? new Date(data.checked_at).toLocaleString()
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('system.checkAll.title', { defaultValue: 'Проверка всех моделей' })}
      footer={null}
    >
      {/* Сводка + таблица результатов */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 text-success">
            <CheckCircle2 className="w-4 h-4" />
            OK: {okCount}
          </span>
          <span className="inline-flex items-center gap-1.5 text-danger">
            <XCircle className="w-4 h-4" />
            {t('system.checkAll.failLabel', { defaultValue: 'Ошибки' })}: {failCount}
          </span>
          {cached && (
            <span className="text-xs text-surface-500">
              {t('system.checkAll.cached', { defaultValue: '(кэш последнего прогона)' })}
            </span>
          )}
        </div>
        {checkedAt && (
          <div className="text-xs text-surface-500">
            {t('system.checkAll.checkedAt', { defaultValue: 'Проверено' })}: {checkedAt}
          </div>
        )}
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-surface-500 uppercase">
                <th className="py-1.5 pr-3">{t('system.checkAll.col.model', { defaultValue: 'Модель' })}</th>
                <th className="py-1.5 pr-3">{t('system.checkAll.col.where', { defaultValue: 'Где используется' })}</th>
                <th className="py-1.5 pr-3">{t('system.checkAll.col.status', { defaultValue: 'Статус' })}</th>
                <th className="py-1.5 pr-3" />
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-3 text-surface-400">
                    {isPending ? '…' : t('system.checkAll.empty', { defaultValue: 'Нет данных' })}
                  </td>
                </tr>
              )}
              {results.map((item) => (
                <CheckAllRow key={`${item.config_id}`} item={item} onGoTo={onGoTo} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

function CheckAllRow({ item, onGoTo }: { item: CheckAllItem; onGoTo: (item: CheckAllItem) => void }) {
  const { t } = useTranslation();
  const where = `${item.module_display_name} × ${item.tier_key}`;
  return (
    <tr className="border-t border-surface-800 align-top">
      <td className="py-1.5 pr-3 font-mono text-xs break-all">
        {item.model}
        {item.carousel && (
          <div className="text-xs text-surface-500">
            {t('system.checkAll.carousel', { defaultValue: 'Карусель' })}: {item.carousel}
          </div>
        )}
      </td>
      <td className="py-1.5 pr-3">
        {where}
        <div className="text-xs text-surface-500">{providerLabel(item.provider)}</div>
      </td>
      <td className="py-1.5 pr-3">
        <span className={cn(
          'inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md border',
          item.ok
            ? 'bg-success-tint/10 text-success border-success-tint/20'
            : 'bg-danger-tint/10 text-danger border-danger-tint/20',
        )}>
          {item.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
          {item.ok ? 'OK' : t('system.checkAll.error', { defaultValue: 'Ошибка' })}
          {typeof item.http_status === 'number' && item.http_status !== 200 && (
            <span className="font-mono"> · {item.http_status}</span>
          )}
        </span>
        {item.error_text && (
          <div className="text-xs text-danger/90 mt-0.5 break-all max-w-md">{item.error_text}</div>
        )}
      </td>
      <td className="py-1.5">
        <button
          type="button"
          className="btn-ghost text-xs px-2 py-1 flex items-center gap-1"
          onClick={() => onGoTo(item)}
        >
          <ExternalLink className="w-3 h-3" />
          {t('system.checkAll.goTo', { defaultValue: 'Перейти' })}
        </button>
      </td>
    </tr>
  );
}
